/**
 * 자동 동기화 컨트롤러 — 결정 #20의 "디바운스 자동 동기화 + 수동 버튼"의 자동 절반.
 *
 * 플랫폼 중립(결정 #10): 실제 데이터 변경 관찰(Dexie liveQuery)과 동기화 실행
 * (runSync)은 web이 주입한다. 이 클래스는 "언제 동기화를 돌릴지"의 타이밍 정책만
 * 순수하게 책임진다 — 그래서 타이머를 주입해 결정적으로 단위 테스트할 수 있다.
 *
 * 풀어야 하는 난제(매우 중요): 동기화의 pull 단계는 원격 레코드를 로컬에 적용
 * (applyRecords)하므로 IndexedDB가 바뀐다. 그 쓰기가 다시 변경 신호(notifyChange)를
 * 일으켜 동기화를 재트리거하면 **무한 에코 루프**가 된다. 두 겹으로 막는다:
 *   1) 인플라이트 가드: 동기화 중 도착한 변경 신호는 큐잉만 하고 즉시 또 돌리지 않는다.
 *   2) 에코 억제 창: 동기화 종료 직후 짧은 시간 동안의 변경 신호는 무시한다(우리 자신의
 *      applyRecords 쓰기가 비동기로 늦게 도착하는 것을 흡수).
 * 설령 에코가 한 번 새어 큐잉돼도, 재동기화는 멱등(받을 것·보낼 것 0)이라 곧 멈춘다.
 */

/** 주입 가능한 타이머(테스트 결정성). 기본은 전역 setTimeout/clearTimeout. */
export interface AutoSyncTimers {
  setTimeout(handler: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  now(): number;
}

const defaultTimers: AutoSyncTimers = {
  setTimeout: (h, ms) => setTimeout(h, ms),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

export interface AutoSyncOptions {
  /** 한 번의 전체 동기화 사이클을 수행한다(예: web의 syncWithGoogleDrive). */
  run: () => Promise<unknown>;
  /**
   * 지금 자동 동기화를 돌려도 되는지(설정 sync.enabled && sync.auto && 신원 연결 등).
   * 매 트리거 직전에 평가하므로, 설정 변경이 다음 사이클부터 즉시 반영된다.
   */
  isEnabled: () => boolean | Promise<boolean>;
  /** 변경 후 동기화까지 대기할 디바운스(ms). 빠른 연속 편집을 한 번으로 묶는다. */
  debounceMs: number;
  /**
   * 동기화 종료 직후 변경 신호를 무시할 시간(ms). 우리 자신의 pull 쓰기가 만든
   * 에코를 흡수한다. 기본 2000ms. 0이면 에코 억제 없음(인플라이트 가드만).
   */
  echoSuppressMs?: number;
  /** 동기화 실패 콜백(throw를 삼켜 자동 루프가 죽지 않게 한다). */
  onError?: (e: unknown) => void;
  /** 사이클 종료(성공/실패) 후 콜백 — UI의 "마지막 동기화" 갱신 등. */
  onSettled?: (ok: boolean) => void;
  /** 타이머 주입(테스트용). 미지정 시 전역 타이머. */
  timers?: AutoSyncTimers;
}

export type AutoSyncState = "stopped" | "idle" | "scheduled" | "syncing";

export class AutoSyncController {
  private readonly run: () => Promise<unknown>;
  private readonly isEnabled: () => boolean | Promise<boolean>;
  private readonly echoSuppressMs: number;
  private readonly onError?: (e: unknown) => void;
  private readonly onSettled?: (ok: boolean) => void;
  private readonly timers: AutoSyncTimers;

  private debounceMs: number;
  private started = false;
  private syncing = false;
  /** 디바운스 타이머 핸들(없으면 null). */
  private timer: unknown = null;
  /** 동기화 중 도착한 변경을 종료 후 한 번 더 처리하기 위한 큐 플래그. */
  private queued = false;
  /**
   * 마지막 동기화 종료 시각(에코 억제 창 계산용). 초기값은 음의 무한대 — 아직 한 번도
   * 동기화하지 않았으므로 첫 변경은 절대 "에코"로 오인되지 않아야 한다. (0으로 두면
   * now()가 0에서 시작하는 환경에서 첫 변경이 억제되는 버그가 난다.)
   */
  private lastSettledAt = Number.NEGATIVE_INFINITY;

  constructor(opts: AutoSyncOptions) {
    this.run = opts.run;
    this.isEnabled = opts.isEnabled;
    this.debounceMs = Math.max(0, opts.debounceMs);
    this.echoSuppressMs = Math.max(0, opts.echoSuppressMs ?? 2000);
    this.onError = opts.onError;
    this.onSettled = opts.onSettled;
    this.timers = opts.timers ?? defaultTimers;
  }

  /** 현재 상태(테스트·디버깅용). */
  get state(): AutoSyncState {
    if (!this.started) return "stopped";
    if (this.syncing) return "syncing";
    if (this.timer !== null) return "scheduled";
    return "idle";
  }

  /** 디바운스 창을 런타임에 조정(설정 변경 반영). */
  setDebounceMs(ms: number): void {
    this.debounceMs = Math.max(0, ms);
  }

  /** 관찰 시작. 호출 전 notifyChange는 무시된다. */
  start(): void {
    this.started = true;
  }

  /** 관찰 중지 + 대기 중 타이머 취소. 진행 중 동기화는 끝까지 가되 큐는 비운다. */
  stop(): void {
    this.started = false;
    this.queued = false;
    this.cancelTimer();
  }

  /**
   * 로컬 데이터가 바뀌었음을 알린다(예: Dexie liveQuery 콜백). 디바운스 후 동기화한다.
   * 동기화 중이거나 에코 억제 창 안이면 적절히 큐잉/무시한다.
   */
  notifyChange(): void {
    if (!this.started) return;

    // 에코 억제 창(결정 #20): 방금 끝낸 동기화의 pull 쓰기가 만든 신호를 흡수.
    if (this.echoSuppressMs > 0 && this.timers.now() - this.lastSettledAt < this.echoSuppressMs) {
      return;
    }

    // 인플라이트 가드: 동기화 중 변경은 종료 후 한 번 더 처리하도록 큐잉만 한다.
    if (this.syncing) {
      this.queued = true;
      return;
    }

    this.schedule();
  }

  /**
   * 디바운스를 건너뛰고 즉시 한 사이클을 시도한다(설정/연결 확인은 그대로 거친다).
   * 이미 동기화 중이면 큐잉한다. 주로 부팅 직후 1회 캐치업에 쓴다.
   */
  async triggerNow(): Promise<void> {
    if (!this.started) return;
    this.cancelTimer();
    if (this.syncing) {
      this.queued = true;
      return;
    }
    await this.runCycle();
  }

  /** 디바운스 타이머를 (재)설정한다. */
  private schedule(): void {
    this.cancelTimer();
    this.timer = this.timers.setTimeout(() => {
      this.timer = null;
      void this.runCycle();
    }, this.debounceMs);
  }

  private cancelTimer(): void {
    if (this.timer !== null) {
      this.timers.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /** 설정/연결을 확인하고 한 사이클을 실행한다. throw는 onError로 흡수. */
  private async runCycle(): Promise<void> {
    if (this.syncing) {
      this.queued = true;
      return;
    }

    let allowed = false;
    try {
      allowed = await this.isEnabled();
    } catch (e) {
      this.onError?.(e);
      allowed = false;
    }
    if (!this.started || !allowed) {
      // 비활성/중지 상태면 조용히 종료(큐도 비운다 — 다음 변경부터 다시 시작).
      this.queued = false;
      return;
    }

    this.syncing = true;
    let ok = false;
    try {
      await this.run();
      ok = true;
    } catch (e) {
      this.onError?.(e);
    } finally {
      this.syncing = false;
      this.lastSettledAt = this.timers.now();
      this.onSettled?.(ok);
    }

    // 동기화 중 들어온 변경이 있으면 한 번 더(에코 억제 창이 자기 쓰기는 걸러낸다).
    if (this.queued) {
      this.queued = false;
      if (this.started) this.schedule();
    }
  }
}
